import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import closeIcon from "../../assets/cross.svg";

const ROLE_OPTIONS = ["OWNER", "BILLING", "ADMIN", "COUNSELOR", "CLASSROOM_VIEWER"];

// Plain-language guidance shown under the role picker so admins pick the right
// access level. Capabilities reflect the API's rank-based permission model
// (Owner > Billing > Admin > Counselor > CLASSROOM_VIEWER, shown to admins as
// "Teacher"). Note: issuing certificates is open to every role — only
// student/staff management is gated.
const ROLE_DESCRIPTION_FALLBACKS = {
  OWNER: "Full access — manage students, staff, certificates and billing. The school's primary administrator.",
  BILLING: "Billing and subscription. Also has the same student & staff management as an Admin.",
  ADMIN: "Manage students, staff and certificates. The standard administrator role.",
  COUNSELOR: "Invite students, view rosters and issue certificates. Can't deactivate students or manage staff.",
  CLASSROOM_VIEWER: "View rosters and issue certificates. Can't invite students or manage staff.",
};

const InviteStaffModal = ({ onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    title: "",
    role: "ADMIN",
  });
  // In flight while the invite request runs. Disables the buttons (no
  // double-invite) and shows a spinner; the parent closes the modal once
  // onConfirm resolves.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValid = form.email.trim().length > 0 && form.role.trim().length > 0;

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!isValid || isSubmitting) return;
    try {
      setIsSubmitting(true);
      // Await so the modal holds its loading state until the invite
      // completes; the parent closes it on success.
      // The API stores a single `name`; join here so the split is ours and
      // exact rather than a guess at the first space downstream.
      const fullName = [form.firstName.trim(), form.lastName.trim()].filter(Boolean).join(" ");
      await onConfirm({
        email: form.email.trim(),
        role: form.role,
        name: fullName || undefined,
        title: form.title.trim() || undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center">
      <div className="w-[560px] bg-white rounded-[20px] overflow-hidden font-['Manrope']">
        <div className="p-6 bg-gradient-to-br from-white to-blue-100 border-b border-blue-100 flex justify-between items-center">
          <h2 className="text-[18px] font-bold text-gray-900">{t("modals.inviteStaff.title")}</h2>
          <button type="button" onClick={onClose} className="p-1">
            <img src={closeIcon} alt={t("modals.common.close")} className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 flex flex-col gap-4">
          <div className="px-4 py-3.5 bg-white rounded-[10px] shadow-[0px_0px_10px_rgba(0,0,0,0.05)] border border-gray-300 flex flex-col gap-1.5">
            <label htmlFor="email" className="text-gray-400 text-sm font-medium">
              {t("modals.inviteStaff.fields.email")}
            </label>
            <input
              id="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder={t("modals.inviteStaff.placeholders.email")}
              className="text-black text-base font-medium outline-none bg-transparent"
            />
          </div>

          {/* First/last captured separately (matching the student invite) so
              the sign-up prefill gets an exact split instead of guessing at a
              space, and the invite email can greet by first name. */}
          <div className="grid grid-cols-2 gap-3">
            <div className="px-4 py-3.5 bg-white rounded-[10px] shadow-[0px_0px_10px_rgba(0,0,0,0.05)] border border-gray-300 flex flex-col gap-1.5">
              <label htmlFor="firstName" className="text-gray-400 text-sm font-medium">
                {t("modals.inviteStaff.fields.firstNameOptional", { defaultValue: "First name (optional)" })}
              </label>
              <input
                id="firstName"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                placeholder={t("modals.inviteStaff.placeholders.firstName", { defaultValue: "Susy" })}
                className="text-black text-base font-medium outline-none bg-transparent"
              />
            </div>
            <div className="px-4 py-3.5 bg-white rounded-[10px] shadow-[0px_0px_10px_rgba(0,0,0,0.05)] border border-gray-300 flex flex-col gap-1.5">
              <label htmlFor="lastName" className="text-gray-400 text-sm font-medium">
                {t("modals.inviteStaff.fields.lastNameOptional", { defaultValue: "Last name (optional)" })}
              </label>
              <input
                id="lastName"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                placeholder={t("modals.inviteStaff.placeholders.lastName", { defaultValue: "Teacher" })}
                className="text-black text-base font-medium outline-none bg-transparent"
              />
            </div>
          </div>

          <div className="px-4 py-3.5 bg-white rounded-[10px] shadow-[0px_0px_10px_rgba(0,0,0,0.05)] border border-gray-300 flex flex-col gap-1.5">
            <label htmlFor="title" className="text-gray-400 text-sm font-medium">
              {t("modals.inviteStaff.fields.titleOptional")}
            </label>
            <input
              id="title"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder={t("modals.inviteStaff.placeholders.title")}
              className="text-black text-base font-medium outline-none bg-transparent"
            />
          </div>

          <div className="px-4 py-3.5 bg-white rounded-[10px] shadow-[0px_0px_10px_rgba(0,0,0,0.05)] border border-gray-300 flex flex-col gap-1.5">
            <label htmlFor="role" className="text-gray-400 text-sm font-medium">
              {t("modals.inviteStaff.fields.role")}
            </label>
            <select
              id="role"
              name="role"
              value={form.role}
              onChange={handleChange}
              className="text-black text-base font-medium outline-none bg-transparent"
            >
              {ROLE_OPTIONS.map((role) => (
                <option key={role} value={role}>
                  {t(`modals.inviteStaff.roles.${role.toLowerCase()}`)}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 leading-snug mt-1">
              {t(`modals.inviteStaff.roleDescriptions.${form.role.toLowerCase()}`, {
                defaultValue: ROLE_DESCRIPTION_FALLBACKS[form.role] || "",
              })}
            </p>
          </div>

          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="h-11 px-4 text-[16px] font-medium text-gray-900 border border-blue-500 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("modals.common.cancel")}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isValid || isSubmitting}
              className="h-11 px-4 text-[16px] font-medium text-white rounded-lg bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting && <Loader2 size={16} className="animate-spin" />}
              {isSubmitting ? t("modals.common.sending", { defaultValue: "Sending…" }) : t("modals.inviteStaff.sendInvite")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteStaffModal;
